import React from 'react';
import {Formik} from 'formik';
import {useRouteMatch} from 'react-router-dom';
import FormCreateShumaView from './FormCreateShuma-view';
import SilentSubmitHandler from '../SilentSubmitHandler';
import ModalPreviewDocument from '../../../../common/components/ModalPreviewDocument';
import {useCreateShumaContext} from '../CreateShumaContext';
import {
  checkRequiredValues
} from '../../../../common/utils/form/checkRequiredValues';
import {decisionTree} from '../../constants/decisionTree';
import {useDecisionTreeShumaTypesOptionsContext} from '../../../../common/components/DecisionTreeOptions';
import {pathnames as P} from '../../../../common/constants/pathnames';
import ButtonSubmitForm from '../ButtonSubmitForm';

const FormCreateShuma = () => {
  const {
    onSubmit,
    onSubmitSuccess,
    clearInspectionData,
    initialValuesStep13,
    options,
    setLoading
  } = useCreateShumaContext();
  const {updateShumaTypes} = useDecisionTreeShumaTypesOptionsContext();

  const {params: {shumaId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const validate = values => {
    const {
      shumaReasonID,
      propertyTypeID,
      bankID,
      shumaTypeID,
      loanTypeID,
      loanText,
      otherPropertyTypeID,
      shumaMazminIdentityID
    } = values;

    let errors = checkRequiredValues({shumaReasonID}, {});
    if (shumaReasonID === decisionTree.rightsEstimation) {
      errors = checkRequiredValues({propertyTypeID}, errors);
    } else {
      errors = checkRequiredValues({
        bankID,
        shumaTypeID,
        propertyTypeID,
        loanTypeID
      }, errors);
    }
    if (loanTypeID && loanTypeID?.value?.toString() === decisionTree.loanTypeID.other) {
      errors = checkRequiredValues({otherPropertyTypeID}, errors);
    }
    if (loanTypeID && loanTypeID?.value?.toString() === decisionTree.loanTypeID.another) {
      errors = checkRequiredValues({loanText}, errors);
    }
    if (!shumaMazminIdentityID?.value) {
      errors = checkRequiredValues({shumaMazminIdentityID}, errors);
    }

    return errors;
  };

  const handleSubmit = values => {
    const data = getData(values);

    Promise.resolve()
      .then(clearInspectionData)
      .then(() => onSubmit(data))
      .then(newShumaID => onSubmitSuccess(newShumaID))
      .catch(console.error);
  };

  const getData = values => {
    const {
      shumaReasonID,
      shumaTypeID,
      propertyTypeID,
      bankID,
      loanTypeID,
      loanText,
      otherPropertyTypeID,
      bankBranchNumber,
      bankBranchName,
      bankClerkFullName,
      bankEmail,
      bankRequestID,
      bankBuildType,
    } = values;

    return shumaReasonID === decisionTree.rightsEstimation
      ? {
        shumaReasonID,
        shumaTypeID: 1,
        propertyTypeID,
        bankID: null,
        loanTypeID: null,
        loanText: '',
        otherPropertyTypeID: null,
        bankBranchNumber: null,
        bankBranchName: null,
        bankClerkFullName: null,
        bankEmail: null,
        bankRequestID: null,
        bankBuildType: null,
      }
      : {
        shumaReasonID,
        shumaTypeID,
        propertyTypeID,
        bankID,
        loanTypeID,
        loanText,
        otherPropertyTypeID: (loanTypeID === decisionTree.loanTypeID.other)
          ? otherPropertyTypeID
          : -1,
        bankBranchNumber,
        bankBranchName,
        bankClerkFullName,
        bankEmail,
        bankRequestID,
        bankBuildType,
      };
  };

  const handleResetDecisionTree = (formikProps) => ({value}) => {
    const {setFieldValue, validateForm} = formikProps;
    setFieldValue('shumaReasonID', value);
    updateShumaTypes(parseInt(value, 10)).catch(console.error);
    switch (value) {
      case decisionTree.bankSecure:
        setFieldValue('bankID', null);
        setFieldValue('bankBranchNumber', '');
        setFieldValue('bankBranchName', '');
        setFieldValue('bankClerkFullName', '');
        setFieldValue('bankEmail', '');
        setFieldValue('bankRequestID', '');
        setFieldValue('bankBuildType', '');
        setFieldValue('shumaTypeID', null);
        setFieldValue('propertyTypeID', null);
        setFieldValue('loanTypeID', null);
        setFieldValue('loanText', '');
        setFieldValue('otherPropertyTypeID', null);
      case decisionTree.rightsEstimation:
      default:
        setFieldValue('shumaTypeID', null);
        setFieldValue('propertyTypeID', null);
    }
    validateForm();
  };

  const handleResetPreviousSteps = (formikProps) => (fieldId, value) => {
    const {setFieldValue} = formikProps;
    switch (fieldId) {
      case ('shumaTypeID'):
        setFieldValue('bankID', null);
        setFieldValue('bankBranchNumber', '');
        setFieldValue('bankBranchName', '');
        setFieldValue('bankClerkFullName', '');
        setFieldValue('bankEmail', '');
        setFieldValue('bankRequestID', '');
        setFieldValue('bankBuildType', '');
        setFieldValue('propertyTypeID', null);
      case ('bankID'):
        setFieldValue('bankBranchNumber', '');
        setFieldValue('bankBranchName', '');
        setFieldValue('bankClerkFullName', '');
        setFieldValue('bankEmail', '');
        setFieldValue('bankRequestID', '');
        setFieldValue('bankBuildType', '');
        setFieldValue('loanTypeID', null);
        setFieldValue('loanText', '');
        setFieldValue('otherPropertyTypeID', null);
      case ('propertyTypeID'):
        setFieldValue('loanTypeID', null);
      case ('loanTypeID'):
      default:
        setFieldValue('loanText', '');
        setFieldValue('loanSubTypeID', '');
        setFieldValue('otherPropertyTypeID', null);
    }
    validateUpdatedForm({fieldId, value, ...formikProps});
  };

  const validateUpdatedForm = ({
                                 fieldId,
                                 value,
                                 validateForm,
                                 values,
                                 setErrors
                               }) => {
    const updatedValues = {...values, [fieldId]: value};
    switch (fieldId) {
      case ('bankID'):
        updatedValues['shumaTypeID'] = initialValuesStep13.shumaTypeID;
      case ('shumaTypeID'):
        updatedValues['propertyTypeID'] = initialValuesStep13.propertyTypeID;
      case ('propertyTypeID'):
        updatedValues['loanTypeID'] = initialValuesStep13.loanTypeID;
      case ('loanTypeID'):
      default:
        updatedValues['loanSubTypeID'] = initialValuesStep13.loanSubTypeID;
        updatedValues['otherPropertyTypeID'] = initialValuesStep13.otherPropertyTypeID;
    }
    validateForm(updatedValues).then(setErrors);
  };

  const handleSubmitForPreview = (values, shumaId, dirty) => {

    Promise.resolve()
      .then(() => onSubmit(values, shumaId, dirty))
      .then(() => onSubmitSuccess())
      .catch(console.error);
  };

  const handleDataSubmitForSave = (values, shumaId, dirty) => {

    Promise.resolve()
      .then(() => setLoading(true))
      .then(() => onSubmit(values, shumaId, dirty))
      .then(() => onSubmitSuccess())
      .catch(console.error)
      .finally(() => setTimeout(() => setLoading(false), 1000));
  };

  return (
    <Formik
      initialValues={initialValuesStep13}
      validate={validate}
      onSubmit={handleSubmit}
      enableReinitialize={options.reinit}
      validateOnChange={true}
      validateOnBlur={false}
      validateOnMount={true}
    >
      {formikProps => (
        <>
          <SilentSubmitHandler
            onSubmit={onSubmit}
            values={formikProps.values}
            shumaId={options.shumaId}
            dirty={formikProps.dirty}
          />
          <ButtonSubmitForm
            onClick={() => {
              handleDataSubmitForSave(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
            disabled={!formikProps.dirty}
          />
          {shumaId && (
            <ModalPreviewDocument
              submitOnOpen={() => {
                handleSubmitForPreview(
                  formikProps.values,
                  shumaId,
                  formikProps.dirty
                );
              }}
            />
          )}
          <FormCreateShumaView
            {...formikProps}
            nextPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 4)}
            resetDecisionTree={handleResetDecisionTree(formikProps)}
            resetPreviousSteps={handleResetPreviousSteps(formikProps)}
          />
        </>
      )}
    </Formik>
  );
};

export default FormCreateShuma;
